import type { BleedAudio } from './bleed-audio';
import { BLEED_MARK_GLYPH, drawBleedGlyph, type GlyphContext } from './bleed-glyph';

/**
 * **傷: uma ferida por marca, e um rasgo de garras quando elas rompem.**
 *
 * ## Quem manda desenhar, e quem nunca manda
 *
 * Este módulo desenha o que a AUTORIDADE diz que existe. Ele não sabe o que é um
 * perfil, um socket, uma cópia, um nível, uma chance ou uma fórmula, e não tem
 * por onde saber: a sua única entrada é uma lista de alvos com
 * `{ key, x, y, diameter, marks[] }`. Nem sequer a vida deles entra.
 *
 * A marca é uma coisa NO INIMIGO. Não é uma decoração do projétil, do canhão nem
 * da nave que atacou, e nada aqui toca em nenhum dos três.
 *
 * ## Porque há um EVENTO aqui, quando o veneno não precisou de um
 *
 * Porque as marcas saírem do estado é AMBÍGUO. Uma gota de veneno só sai por uma
 * razão - pagou as seis - e uma cena podia inferi-lo. Uma marca de sangramento
 * sai por três: foi detonada, o alvo morreu, o autor morreu. Desenhar o rasgo em
 * todas as três acenderia um efeito sobre um casco que apenas foi abatido.
 *
 * ## Um objeto por ALVO, e não um por marca
 *
 * Vinte marcas num mob são vinte 傷 e **uma** `Image`. Os caracteres são escritos
 * numa textura de canvas própria desse alvo, redesenhada só quando a contagem
 * muda - portanto vinte marcas custam vinte escritas num canvas, uma vez, e não
 * vinte objetos de jogo nem um desenho por quadro.
 *
 * Cada 傷 continua individualmente visível e contável. Não há `+N`, não há número
 * agregado, não há limite silencioso, e o tecto de marcas que este sistema
 * desenha é o número que a autoridade mandou - qualquer que ele seja.
 */

/* -------------------------------------------------------------------------- */
/*  A direção de arte                                                         */
/* -------------------------------------------------------------------------- */

/**
 * A cor de uma marca PARADA: carmesim.
 *
 * Aplicada por `tint` sobre um carácter branco, portanto é exatamente esta cor e
 * não uma multiplicação dela. Ver a nota em `drawBleedGlyph`.
 */
const MARK_TINT = 0xd21c3a;

/** E a de uma marca no instante em que rompe: quase branco. */
const BURST_TINT = 0xfff1f2;

/** O corpo do rasgo de garras: carmesim vivo. */
const CLAW_BODY = 0xe11d3a;

/** E o seu núcleo, quase branco, por dentro do corpo. */
const CLAW_CORE = 0xfff1f2;

/**
 * **Quanto da largura do alvo uma marca ocupa: 34 %.**
 *
 * ## Porque NÃO são os 14 % da gota de veneno
 *
 * Porque um kanji precisa de mais píxeis do que uma forma geométrica. A 14 %,
 * um casco de duelo de 64 px dava um 傷 de 9 px, e a 22 % dava 14 - e a catorze
 * o dono olhou para o ecrã e disse que quase não se via nem se entendia. Tinha
 * razão: os traços internos do 傷 fecham-se e o que fica é uma mancha vermelha.
 *
 * A 34 %, o mesmo casco dá 21 px, que é onde o carácter volta a ser escrita.
 *
 * A paridade com o veneno era uma decisão MINHA, não do dono, e escolher entre
 * ela e um sinal legível não é uma escolha: um símbolo que não se distingue não
 * é um símbolo. O que continua a separar as duas marcas é a FAIXA, a forma e a
 * cor - e agora também o tamanho, deliberadamente.
 *
 * ## O que ela continua a garantir
 *
 * Escala com o diâmetro DESENHADO, portanto zoom e escala visual acompanham
 * sozinhos: um mob de 84 px leva 28 px e um boss de 252 leva 85.
 */
export const BLEED_MARK_TARGET_RATIO = 0.34;

/**
 * O tamanho MÍNIMO de uma marca, em píxeis da cena.
 *
 * Não é um teto disfarçado e não limita coisa nenhuma que um item conceda: é o
 * chão de legibilidade de um kanji.
 *
 * CATORZE: é o tamanho a que o 傷 ainda se lê como carácter e não como mancha,
 * medido no ecrã e não escolhido de cabeça. Com os 34 % acima, ele só entra
 * abaixo de 41 px de alvo, que é território de zoom mínimo sobre um mob
 * pequeno - portanto continua a ser um chão, e não uma proporção disfarçada.
 */
export const BLEED_MARK_MIN_PX = 14;

/**
 * A altura de uma marca em múltiplos da largura: uma. Um kanji é QUADRADO.
 *
 * Escrito como constante em vez de `1` espalhado porque a disposição lê-o, e
 * porque o dia em que a marca deixar de ser um carácter é o dia em que este
 * número passa a ser outro.
 */
export const BLEED_MARK_ASPECT = 1;

/** Quanto espaço horizontal separa duas marcas, em múltiplos da largura. */
const GAP_RATIO = 0.28;

/** E quanto separa duas linhas, em múltiplos da altura. */
const ROW_GAP_RATIO = 0.22;

/* -------------------------------------------------------------------------- */
/*  O rasgo de garras                                                         */
/* -------------------------------------------------------------------------- */

/**
 * **A detonação é uma SEQUÊNCIA, e não um instante** (decisão do dono).
 *
 * ## O que mudou, e quem o decidiu
 *
 * A primeira versão deste gate desenhava tudo em 150 ms - foi o que o contrato
 * escrito pediu, e foi o que se construiu. Depois de o ver no ecrã, o dono pediu
 * outra coisa: **uma garra por marca, uma a cada décimo de segundo, cada uma com
 * o seu som**, e só no fim o corte grande e o número.
 *
 * Isso substitui o «~150 ms» do §7 por uma duração que depende da contagem. É
 * uma decisão dele sobre a folha dele, tomada a olhar para o resultado, e está
 * registada aqui porque contradiz o texto que a originou.
 *
 * ## O que isso custa, dito uma vez
 *
 * O número de dano passa a aparecer até dois segundos e meio depois de a vida
 * ter descido. A barra desce quando a autoridade manda - isso não mudou e não
 * pode mudar - portanto durante a sequência o jogador vê a vida a cair antes de
 * ler quanto. É o preço de ver as marcas serem cobradas uma a uma.
 */

/** O intervalo entre duas garras: um décimo de segundo, como foi pedido. */
export const BLEED_CLAW_INTERVAL_MS = 100;

/**
 * Quanto tempo UMA garra fica no ecrã.
 *
 * Mais do que o intervalo, e é deliberado: com 220 ms contra 100, cada garra
 * ainda está a desvanecer quando a seguinte entra. É isso que faz a sequência
 * ler-se como um ataque contínuo em vez de um metrónomo.
 */
export const BLEED_CLAW_LIFE_MS = 220;

/**
 * A convergência das marcas para o casco, antes da primeira garra.
 *
 * Curta: é a antecipação do golpe, e uma antecipação longa numa sequência que já
 * é longa transformaria o princípio em espera.
 */
export const BLEED_CONVERGE_MS = 90;

/** E o corte final, que fecha a sequência e traz o número. */
export const BLEED_FINALE_MS = 340;

/**
 * **Quando o corte final abre**, para uma detonação de `marks` marcas.
 *
 * Depois de a última garra ter nascido e do intervalo dela: o corte é a resposta
 * à sequência, não o seu último elemento.
 */
export function bleedFinaleAt(marks: number): number {
  return BLEED_CONVERGE_MS + Math.max(1, bleedClawCount(marks)) * BLEED_CLAW_INTERVAL_MS;
}

/** E quanto dura o efeito inteiro, do primeiro quadro ao último. */
export function bleedBurstDuration(marks: number): number {
  return bleedFinaleAt(marks) + BLEED_FINALE_MS;
}

/**
 * Quantos riscos tem uma garra. Três, que é o que faz um rasgo ser uma garra.
 *
 * Dois leem-se como um "igual" e quatro fecham-se numa mancha à velocidade a que
 * isto passa. Três é a forma que o olho reconhece em noventa milissegundos.
 */
const CLAW_STROKES = 3;

/**
 * **O tecto de garras que um rebentamento desenha.**
 *
 * ## Porque subiu de seis para vinte e quatro
 *
 * Porque o dono pediu, depois de ver seis no ecrã, que **cada marca seja uma
 * garra diferente, um arranhão diferente**. A seis, uma detonação de dezoito
 * marcas desenhava o mesmo que uma de seis - e o que se via não tinha relação
 * nenhuma com o que tinha sido acumulado.
 *
 * O §7 continua a exigir um limite visual, e ele continua a existir: é este.
 * Vinte e quatro é o que uma detonação ordinária nunca alcança - marcas nascem a
 * 13 % dos acertos diretos no nível máximo - portanto na prática cada marca tem
 * a sua garra, e o tecto é uma rede de segurança contra um caso extremo, não uma
 * política de agregação.
 *
 * Acima dele a intensidade continua a subir e a contagem não: é onde o §7 ganha.
 */
export const BLEED_MAX_CLAWS = 24;

/**
 * Quantas garras uma detonação de `marks` marcas desenha.
 *
 * **Uma marca, uma garra**, até {@link BLEED_MAX_CLAWS}. Exportada porque é o
 * contrato entre a contagem autoritativa e o que se vê, e uma prova tem de o
 * poder ler sem abrir um canvas.
 */
export function bleedClawCount(marks: number): number {
  if (!Number.isFinite(marks) || marks <= 0) {
    return 0;
  }
  return Math.max(1, Math.min(BLEED_MAX_CLAWS, Math.round(marks)));
}

/**
 * **Quanto do casco um arranhão atravessa: 1,25 diâmetros.**
 *
 * Acima de um, e é o pedido: o rasgo tem de **cobrir a nave toda**. A 0,62 -
 * o valor que o dono viu - o golpe cabia dentro do casco e lia-se como um risco
 * por cima dele; acima de um, ele ENTRA de um lado e SAI do outro, que é o que
 * um golpe de garra faz.
 *
 * Multiplicado pelo diâmetro DESENHADO, portanto acompanha zoom e escala sem
 * ninguém afinar nada.
 */
export const BLEED_CLAW_REACH_RATIO = 1.25;

/**
 * **O teto do pool de rebentamentos, DERIVADO do pior caso legítimo de hoje.**
 *
 * ## Recontado quando o efeito passou a ser uma SEQUÊNCIA
 *
 * O teto anterior era 86 e vinha de um efeito de 150 ms. Um efeito que agora
 * dura `bleedBurstDuration(marks)` - até cerca de 2,8 s com o tecto de garras -
 * fica vivo dezoito vezes mais tempo, e um teto derivado da duração antiga
 * deixaria de descrever o que quer que fosse.
 *
 * ## A caça, que é o maior dos dois
 *
 * `HUNT_BALANCE.mobs.maxAlive` é 42, e o boss não conta contra ele: são **43
 * alvos** que podem carregar marcas ao mesmo tempo, e uma ordem detona-os a
 * todos de uma vez.
 *
 * Quantas ORDENS cabem numa vida de efeito: cada uma custa um pedido e uma
 * resposta ao servidor, que numa máquina local ronda os 150 ms com folga -
 * portanto **quatro** cabem nos 2,8 s do pior caso. `43 x 4` = **172**.
 *
 * ## O duelo, que é menor
 *
 * Seis cascos, três inimigos cada, e um autor pode voltar a detonar de tique em
 * tique. Mas uma ordem consome TODAS as marcas dele, e as seguintes não têm nada
 * para rebentar até novos acertos marcarem - portanto `6 x 3 x 4` = **72**.
 *
 * O maior dos dois ganha. Não é um palpite nem uma folga: subir `maxAlive`, o
 * intervalo entre garras ou o tecto de garras muda a conta, e a prova que o
 * verifica falha nesse dia.
 */
export const BLEED_BURST_POOL_CEILING = 172;

/* -------------------------------------------------------------------------- */
/*  A disposição                                                              */
/* -------------------------------------------------------------------------- */

export interface BleedPoint {
  readonly x: number;
  readonly y: number;
}

/**
 * **Onde cada marca fica**, em coordenadas locais da âncora.
 *
 * A âncora é o topo da faixa que o veneno ocupa, portanto tudo aqui tem `y`
 * NEGATIVO: as marcas ficam acima dela e nunca sobre ela, sobre a barra de vida,
 * sobre o casco, sobre um nome, sobre um marcador de equipa ou sobre uma gota.
 *
 * As linhas enchem-se de baixo para cima e cada uma é CENTRADA. A capacidade de
 * cada linha sai da largura desenhada do alvo - não de um número escolhido - por
 * isso um boss põe mais por linha e um mob pequeno põe menos, sem que ninguém
 * decida nada.
 *
 * Nunca devolve menos posições do que marcas: não há teto, não há `+N`, e um
 * conjunto grande produz mais linhas em vez de menos marcas.
 */
export function bleedMarkLayout(count: number, markWidth: number, maxWidth: number): BleedPoint[] {
  if (count <= 0 || markWidth <= 0) {
    return [];
  }
  const step = markWidth * (1 + GAP_RATIO);
  const perRow = Math.max(1, Math.floor(maxWidth / step));
  const height = markWidth * BLEED_MARK_ASPECT;
  const rowStep = height * (1 + ROW_GAP_RATIO);
  const places: BleedPoint[] = [];
  for (let index = 0; index < count; index += 1) {
    const row = Math.floor(index / perRow);
    const column = index % perRow;
    const inRow = Math.min(perRow, count - row * perRow);
    const rowWidth = (inRow - 1) * step;
    places.push({
      x: column * step - rowWidth / 2,
      /*
       * Meia altura para a primeira linha ficar INTEIRA acima da âncora, e
       * `rowStep` por cada linha acima dela.
       */
      y: -(height / 2) - row * rowStep,
    });
  }
  return places;
}

/**
 * **Quanto espaço as marcas ocupam ACIMA da âncora**, em píxeis da cena.
 *
 * A gémea exata de `poisonBandHeight`, e existe pela mesma razão: uma faixa que
 * queira ficar acima desta precisa de perguntar qual é a altura dela, e nunca de
 * afinar um número à mão.
 */
export function bleedBandHeight(count: number, markWidth: number, maxWidth: number): number {
  const places = bleedMarkLayout(count, markWidth, maxWidth);
  if (places.length === 0) {
    return 0;
  }
  const height = markWidth * BLEED_MARK_ASPECT;
  let highest = 0;
  for (const place of places) {
    highest = Math.max(highest, -place.y + height / 2);
  }
  return highest;
}

/** A largura de uma marca para um alvo deste diâmetro. Uma fórmula, um dono. */
export function bleedMarkWidth(diameter: number): number {
  return Math.max(BLEED_MARK_MIN_PX, diameter * BLEED_MARK_TARGET_RATIO);
}

/**
 * **Quanto espaço horizontal a faixa pode usar.**
 *
 * ## O defeito que isto corrige
 *
 * A largura era o DIÂMETRO do alvo. Num duelo, um casco de 64 px com marcas de
 * 21 px cabia duas por linha - portanto dezoito marcas eram nove linhas, uma
 * torre mais alta do que a nave, que se sobrepunha ao casco e às gotas de
 * veneno. O dono viu exatamente isso e disse-o.
 *
 * O contrato proíbe esconder marcas, agregar num `+N` ou pôr um teto, portanto
 * a saída não é desenhar menos: é desenhar mais LARGO. Uma faixa larga e baixa
 * ocupa a mesma banda de sempre e não invade nada.
 *
 * ## Porque estes dois números
 *
 * `2,6 x diâmetro` é o que faz uma linha caber seis marcas num casco de duelo -
 * dezoito marcas passam de nove linhas para três. O segundo termo é o que
 * garante o mesmo em cima de um alvo minúsculo, onde a proporção sozinha daria
 * uma linha de duas: pelo menos seis marcas cabem numa linha, sempre.
 *
 * Continua a não haver teto: quarenta marcas dão sete linhas, e todas se veem.
 */
export function bleedBandWidth(diameter: number, markWidth: number): number {
  return Math.max(diameter * 2.6, markWidth * (1 + GAP_RATIO) * 6);
}

/* -------------------------------------------------------------------------- */
/*  A cena, pela interface mínima                                             */
/* -------------------------------------------------------------------------- */

/**
 * **A camada de um alvo: uma textura de canvas, e a `Image` que a mostra.**
 *
 * ## Porque NÃO uma `RenderTexture`
 *
 * Porque no Phaser 4.2.1 ela não desenha. Uma primeira versão deste módulo
 * carimbava os 傷 numa `RenderTexture` por alvo, o que é o que a documentação
 * descreve e o que os tipos aceitam - e a arena ficou sem uma única marca
 * vermelha, com zero erros na consola.
 *
 * A prova está em `probe`: num Chromium REAL, com janela e GPU, um retângulo
 * comum dá 1600 píxeis, uma `Image` com textura de canvas dá 512, e uma
 * `RenderTexture` - carimbada, ou apenas PREENCHIDA com `fill()` - dá **zero**.
 * O `stamp` existe, aceita os argumentos, não lança nada e não pinta nada.
 *
 * Nenhum tipo podia ter apanhado isto: um método que existe, aceita o que se lhe
 * dá e não faz nada é indistinguível de um que funciona, para o compilador. Só
 * um píxel lido de um ecrã real o distingue.
 *
 * ## O que substitui, sem perder o contrato
 *
 * Uma `CanvasTexture` por alvo, com os N glifos desenhados lá dentro por
 * `drawImage`, mostrada por **uma** `Image` tingida. Continua a ser um objeto de
 * display por alvo e uma rasterização do carácter para o jogo inteiro; o que
 * muda é que o desenho passa pelo canvas 2D em vez de um framebuffer.
 */
export interface BleedLayerContext extends GlyphContext {
  drawImage(
    source: CanvasImageSource,
    sx: number,
    sy: number,
    sw: number,
    sh: number,
    dx: number,
    dy: number,
    dw: number,
    dh: number,
  ): void;
}

/** A textura de canvas de um alvo: redimensiona-se, desenha-se, envia-se. */
export interface BleedLayerTexture {
  getContext(): BleedLayerContext;
  setSize(width: number, height: number): unknown;
  refresh(): unknown;
}

/** E a `Image` que a mostra: move-se, tinge-se, larga-se. */
export interface BleedLayerImage {
  /**
   * Relê a moldura da textura.
   *
   * Necessário depois de a textura mudar de tamanho: a `Image` guarda a largura,
   * a altura e o deslocamento da ORIGEM da moldura que tinha quando foi criada,
   * e nada a manda relê-los. Uma camada que crescesse de 21 px para 70 px de
   * altura continuava a ser posicionada como se tivesse 21 - e o bloco de marcas
   * descia por cima do casco e das gotas de veneno, que foi exatamente o que se
   * viu no ecrã.
   */
  setTexture(key: string): unknown;
  setDepth(value: number): unknown;
  setVisible(value: boolean): unknown;
  setPosition(x: number, y: number): unknown;
  setOrigin(x: number, y: number): unknown;
  setTint(value: number): unknown;
  destroy(): void;
}

/** E o `Graphics` do rasgo, que é desenhado e não carimbado. */
export interface BleedGraphics {
  clear(): BleedGraphics;
  fillStyle(color: number, alpha?: number): BleedGraphics;
  fillPoints(
    points: readonly BleedPoint[],
    closeShape?: boolean,
    closePath?: boolean,
  ): BleedGraphics;
  setDepth(value: number): BleedGraphics;
  setVisible(value: boolean): BleedGraphics;
  setPosition(x: number, y: number): BleedGraphics;
  destroy(): void;
}

export interface BleedScene {
  add: {
    graphics(config?: unknown): BleedGraphics;
    image(x: number, y: number, key: string): BleedLayerImage;
  };
  textures: BleedTextureManager;
}

/**
 * O gestor de texturas, reduzido ao que este módulo lhe pede.
 *
 * `remove` está aqui porque uma camada por alvo é uma textura por alvo, e um
 * alvo que deixa de existir tem de largar a sua - senão um duelo longo deixa no
 * gestor uma textura por casco que alguma vez foi marcado.
 */
export interface BleedTextureManager {
  exists(key: string): boolean;
  createCanvas(key: string, width: number, height: number): BleedLayerTexture | null;
  remove(key: string): unknown;
}

/* -------------------------------------------------------------------------- */
/*  O que a cena entrega                                                      */
/* -------------------------------------------------------------------------- */

/** Uma marca, reduzida ao que um 傷 precisa. */
export interface BleedMark {
  /** A identidade autoritativa. É ela que faz a marca deste quadro ser a mesma. */
  readonly id: number;
}

/** Um alvo marcado, já em píxeis da cena. */
export interface BleedMarkTarget {
  /** A identidade do alvo. Um mob por id, um casco por assento. */
  readonly key: string;
  /** O centro horizontal do alvo. */
  readonly x: number;
  /**
   * A base da faixa das marcas. Os 傷 ficam ACIMA disto.
   *
   * A cena calcula-o a partir do topo da barra de vida MAIS a altura da faixa do
   * veneno, e é isso que cumpre o §"ocupar uma faixa diferente da usada pelo
   * Veneno" sem que nenhum dos dois módulos conheça o outro.
   */
  readonly y: number;
  /** O diâmetro DESENHADO do alvo, em píxeis da cena. */
  readonly diameter: number;
  readonly marks: readonly BleedMark[];
}

/**
 * Uma detonação CONFIRMADA sobre um alvo, já em píxeis da cena.
 *
 * O que a cena entrega é o que a autoridade disse mais onde isso está: a
 * contagem e a identidade vêm do evento, e a posição e o diâmetro da mesma fonte
 * por onde o alvo já é desenhado.
 */
export interface BleedBurst {
  /** `(autor, tique, alvo)` - a chave de deduplicação de um frame repetido. */
  readonly key: string;
  readonly x: number;
  readonly y: number;
  readonly diameter: number;
  /** Quantas marcas rebentaram. Decide quantas garras, até ao tecto. */
  readonly marks: number;
  /**
   * A semente da variação, DERIVADA da identidade autoritativa.
   *
   * O §7 exige que a variação venha da identidade, do tique ou do evento quando
   * precisa de coincidir entre clientes - e aqui precisa: dois jogadores a ver o
   * mesmo duelo têm de ver as mesmas garras. Nunca `Math.random()`.
   */
  readonly seed: number;
  /**
   * O dano TOTAL que a autoridade aplicou, para o número que fecha a sequência.
   *
   * Carregado aqui e não lido pela cena no fim, porque o fim acontece até dois
   * segundos e meio depois: nessa altura o frame que trouxe este valor já foi
   * substituído muitas vezes. O que se imprime é o que a autoridade disse no
   * instante do golpe, e este campo é onde ele fica guardado.
   *
   * Este módulo não o usa para nada - não o formata, não o compara e não o
   * soma. Ele só o devolve a quem sabe desenhá-lo.
   */
  readonly damage: number;
}

/* -------------------------------------------------------------------------- */
/*  O sistema das marcas                                                      */
/* -------------------------------------------------------------------------- */

interface LiveTarget {
  /** A chave da textura deste alvo. Larga-se com ele. */
  readonly textureKey: string;
  readonly texture: BleedLayerTexture;
  readonly image: BleedLayerImage;
  /** O que foi desenhado da última vez. Um redesenho só acontece se isto mudar. */
  drawn: string;
  /** O tamanho atual da textura, para só a redimensionar quando precisa. */
  width: number;
  height: number;
}

/**
 * **O sistema das marcas.** Um por cena, dono de tudo o que cria.
 *
 * Nada aqui é global e nada sobrevive a `destroy()`: os objetos são deste
 * sistema, não há tween registado, e a única textura no gestor da cena é o
 * carácter - criado uma vez e partilhado por tudo.
 */
export class BleedMarkSystem {
  private readonly targets = new Map<string, LiveTarget>();
  private disposed = false;

  constructor(
    private readonly scene: BleedScene,
    private readonly depth = 43,
    /**
     * O prefixo das chaves das texturas deste sistema.
     *
     * Parametrizado porque o gestor de texturas é do JOGO e não da cena: a caça
     * e o duelo podem ter um sistema cada, e duas cenas a criar
     * `bleed-layer-a1` seriam a mesma textura partilhada por engano.
     */
    private readonly namespace = 'bleed-layer',
  ) {}

  /**
   * Põe o ecrã de acordo com o estado autoritativo deste quadro.
   *
   * Cada alvo é REPOSICIONADO - sempre, porque ele move-se - e só é REDESENHADO
   * quando a assinatura do que ele mostra mudou: o conjunto de ids, o tamanho de
   * uma marca ou a largura disponível. Um mob marcado e parado não custa
   * carimbo nenhum.
   *
   * Um alvo que desapareceu da lista larga o seu objeto: foi detonado, morreu,
   * ou o autor morreu. As três apagam a marca no mesmo quadro, e qual das três
   * foi é o que o rebentamento diz - não este método.
   */
  sync(targets: readonly BleedMarkTarget[]): void {
    if (this.disposed) {
      return;
    }
    const seen = new Set<string>();
    for (const target of targets) {
      if (target.marks.length === 0) {
        continue;
      }
      seen.add(target.key);
      this.syncTarget(target);
    }
    for (const [key, live] of this.targets) {
      if (!seen.has(key)) {
        this.release(live);
        this.targets.delete(key);
      }
    }
  }

  /** Quantos alvos têm marca agora. Para as provas e para as medições. */
  liveCount(): number {
    return this.targets.size;
  }

  /**
   * Apaga tudo o que está no ecrã, sem o sistema deixar de funcionar.
   *
   * Para uma CORREÇÃO ou uma substituição de mundo: o que estava desenhado
   * pertencia a um estado que já não existe, e o próximo `sync` desenha o novo.
   */
  clear(): void {
    for (const live of this.targets.values()) {
      this.release(live);
    }
    this.targets.clear();
  }

  /** Larga tudo. Depois disto o sistema não desenha mais nada. */
  destroy(): void {
    this.disposed = true;
    this.clear();
  }

  /**
   * Larga um alvo: o objeto de display E a textura dele.
   *
   * As duas coisas, e nesta ordem. A textura vive no gestor do JOGO e não morre
   * com a `Image`: esquecê-la deixaria no gestor uma textura por casco alguma
   * vez marcado, que é uma fuga que só se vê ao fim de horas.
   */
  private release(live: LiveTarget): void {
    live.image.destroy();
    this.scene.textures.remove(live.textureKey);
  }

  private syncTarget(target: BleedMarkTarget): void {
    const width = bleedMarkWidth(target.diameter);
    const span = bleedBandWidth(target.diameter, width);
    const places = bleedMarkLayout(target.marks.length, width, span);
    const band = bleedBandHeight(target.marks.length, width, span);

    let live = this.targets.get(target.key);
    if (!live) {
      const textureKey = `${this.namespace}:${target.key}`;
      /*
       * Uma textura órfã de um alvo com o mesmo nome numa corrida anterior faria
       * `createCanvas` devolver `null`. Largar antes de criar é o que torna este
       * caminho idempotente entre reinícios de cena.
       */
      if (this.scene.textures.exists(textureKey)) {
        this.scene.textures.remove(textureKey);
      }
      const texture = this.scene.textures.createCanvas(
        textureKey,
        Math.max(1, Math.ceil(span)),
        Math.max(1, Math.ceil(Math.max(band, width))),
      );
      if (!texture) {
        return;
      }
      const image = this.scene.add.image(target.x, target.y, textureKey);
      image.setDepth(this.depth);
      image.setVisible(true);
      /*
       * A origem no MEIO EM BAIXO: a camada é ancorada pelo ponto que a cena
       * entrega, que é a base da faixa. Assim o alvo a mover-se custa um
       * `setPosition` e a faixa cresce para cima sozinha.
       */
      image.setOrigin(0.5, 1);
      /*
       * A COR, uma vez. O carácter é escrito a branco e é este `tint` que o faz
       * carmesim - ver a nota em `drawBleedGlyph`.
       */
      image.setTint(MARK_TINT);
      live = {
        textureKey,
        texture,
        image,
        drawn: '',
        width: Math.max(1, Math.ceil(span)),
        height: Math.max(1, Math.ceil(Math.max(band, width))),
      };
      this.targets.set(target.key, live);
    }
    /* A posição, SEMPRE. O alvo move-se e as marcas vão com ele. */
    live.image.setPosition(target.x, target.y);

    /*
     * A ASSINATURA do que se vai ver: ids, tamanho e largura disponível.
     *
     * Não há fase de pulso aqui, ao contrário do veneno, e a ausência é o
     * contrato: uma marca de sangramento não tem relógio, portanto não tem nada
     * que possa mudar sozinho entre dois quadros.
     */
    const signature = `${width.toFixed(2)}|${span.toFixed(2)}|${target.marks
      .map((mark) => mark.id)
      .join(',')}`;
    if (signature === live.drawn) {
      return;
    }
    live.drawn = signature;

    /*
     * A moldura só muda quando o número de LINHAS muda.
     *
     * `setSize` e não uma textura nova: é ele que redimensiona o canvas E os
     * dados da textura de uma vez. Redimensionar limpa o canvas, o que não
     * importa - o desenho todo vem a seguir.
     */
    const canvasWidth = Math.max(1, Math.ceil(span));
    const canvasHeight = Math.max(1, Math.ceil(Math.max(band, width)));
    if (live.width !== canvasWidth || live.height !== canvasHeight) {
      live.texture.setSize(canvasWidth, canvasHeight);
      live.width = canvasWidth;
      live.height = canvasHeight;
      /*
       * E a `Image` relê a moldura, SEMPRE que a textura mudou de tamanho.
       *
       * Sem isto ela continua a usar as medidas antigas para calcular onde a
       * origem (0.5, 1) cai, e a faixa deixa de estar ancorada onde a cena a
       * mandou pôr. Ver a nota em `BleedLayerImage.setTexture`.
       */
      live.image.setTexture(live.textureKey);
      live.image.setOrigin(0.5, 1);
    }

    const context = live.texture.getContext();
    context.clearRect(0, 0, canvasWidth, canvasHeight);
    for (let index = 0; index < target.marks.length; index += 1) {
      const place = places[index];
      if (place === undefined) {
        continue;
      }
      /*
       * Em coordenadas da TEXTURA: `(0, 0)` é o canto superior esquerdo do
       * canvas, e a disposição conta para cima a partir da base - portanto a
       * base é `canvasHeight` e `place.y`, que é negativo, sobe a partir dela.
       */
      drawBleedGlyph(
        context,
        BLEED_MARK_GLYPH,
        canvasWidth / 2 + place.x,
        canvasHeight + place.y,
        width,
      );
    }
    /* E só agora a textura sobe para a GPU: um envio por redesenho, não N. */
    live.texture.refresh();
  }
}

/* -------------------------------------------------------------------------- */
/*  O sistema dos rebentamentos                                               */
/* -------------------------------------------------------------------------- */

interface LiveBurst {
  readonly graphics: BleedGraphics;
  readonly burst: BleedBurst;
  readonly startedAt: number;
  /** De onde as marcas convergem, em coordenadas locais do casco. */
  readonly from: readonly BleedPoint[];
  /** Uma garra: por onde entra, para onde sai, e quando. */
  readonly claws: readonly Claw[];
  /** Quantas garras já anunciaram a sua entrada. Um som cada, e nunca dois. */
  announced: number;
  /** Se o corte final já abriu. O número e o seu som saem daqui, uma vez. */
  finaled: boolean;
}

/**
 * **Um arranhão, e cada um é o seu.**
 *
 * Cinco propriedades e não duas, porque o pedido foi que cada marca seja *«uma
 * garra diferente, uma arranhada diferente»*. Duas garras com o mesmo ângulo e
 * comprimentos iguais lêem-se como a mesma garra desenhada duas vezes; estas
 * diferem no ângulo, no ponto do casco onde caem, no comprimento, na abertura
 * entre os riscos e no instante em que entram.
 */
interface Claw {
  readonly angle: number;
  /** Onde no casco este golpe cai, em frações do raio. Nunca todos no centro. */
  readonly pivot: BleedPoint;
  /** O comprimento deste golpe, em múltiplos do alcance base. */
  readonly length: number;
  /** A abertura entre os três riscos deste golpe. */
  readonly spread: number;
  /** O instante EM MILISSEGUNDOS em que esta garra entra, desde o início. */
  readonly at: number;
}

/**
 * **O sistema dos rebentamentos.** Um efeito curto por ALVO detonado.
 *
 * ## Um por alvo, e nunca um por marca
 *
 * Dez marcas num casco produzem UM rasgo, com o número de garras a seguir a
 * contagem até {@link BLEED_MAX_CLAWS} - não dez efeitos sobrepostos, que é a
 * definição de uma explosão.
 *
 * ## Um pool, e o teto é derivado
 *
 * Ver {@link BLEED_BURST_POOL_CEILING}. Um efeito que chegue com o pool cheio é
 * DEIXADO CAIR e contado, nunca posto em fila: uma fila desenharia o rasgo
 * depois de a vida já ter descido.
 */
export class BleedBurstSystem {
  private readonly live = new Map<string, LiveBurst>();
  private readonly idle: BleedGraphics[] = [];
  private dropped = 0;
  private disposed = false;

  constructor(
    private readonly scene: BleedScene,
    private readonly depth = 44,
    private readonly ceiling = BLEED_BURST_POOL_CEILING,
    /**
     * O som, ou `null` quando não há contexto de áudio.
     *
     * Injetado e nunca construído aqui: este módulo desenha, e um módulo que
     * desenha não deve saber o que é um `AudioContext`. É também o que permite
     * provar a SEQUÊNCIA - uma garra, um som - com um duplo que apenas conta.
     */
    private readonly audio: BleedAudio | null = null,
    /**
     * O que fazer quando o corte final abre.
     *
     * É aqui que o número de dano total nasce, e é a cena que o desenha: o
     * pedido do dono foi que ele apareça **só no fim**, e o fim é um instante que
     * só este sistema conhece.
     */
    private readonly onFinale: ((burst: BleedBurst) => void) | null = null,
  ) {}

  /**
   * Começa um rebentamento, ou deixa-o cair porque o pool está cheio.
   *
   * Idempotente por `key`: o mesmo `(autor, tique, alvo)` recebido duas vezes -
   * um frame repetido, uma correção - desenha uma vez.
   */
  spawn(burst: BleedBurst, nowMs: number): void {
    if (this.disposed || this.live.has(burst.key)) {
      return;
    }
    if (this.live.size >= this.ceiling) {
      this.dropped += 1;
      return;
    }
    const graphics = this.idle.pop() ?? this.scene.add.graphics();
    graphics.setDepth(this.depth).setVisible(true);
    const width = bleedMarkWidth(burst.diameter);
    this.live.set(burst.key, {
      graphics,
      burst,
      startedAt: nowMs,
      /*
       * De ONDE as marcas convergem: exatamente onde elas estavam desenhadas.
       *
       * O mesmo `bleedMarkLayout` que as pôs lá, e não uma cópia da fórmula: se
       * as duas divergissem, o efeito começaria ao lado da marca que ele está a
       * consumir - que é a única coisa que o jogador vai estar a olhar.
       */
      from: bleedMarkLayout(burst.marks, width, Math.max(width, burst.diameter)),
      claws: clawsFrom(burst.seed, bleedClawCount(burst.marks)),
      announced: 0,
      finaled: false,
    });
  }

  /** Quantos efeitos estão vivos agora. */
  liveCount(): number {
    return this.live.size;
  }

  /** Quantos objetos gráficos o pool guarda parados. */
  idleCount(): number {
    return this.idle.length;
  }

  /** Quantos efeitos foram deixados cair por o pool estar cheio. */
  droppedCount(): number {
    return this.dropped;
  }

  /**
   * Desenha o quadro, e larga o que já acabou.
   *
   * @param nowMs o relógio da CENA. Nunca `Date.now()`: um efeito é apresentação,
   *   e a cena é quem sabe quando o seu próprio tempo pausou.
   */
  update(nowMs: number): void {
    if (this.disposed) {
      return;
    }
    for (const [key, live] of this.live) {
      const elapsed = nowMs - live.startedAt;
      if (elapsed < 0 || elapsed >= bleedBurstDuration(live.burst.marks)) {
        this.retire(key, live);
        continue;
      }
      this.announce(live, elapsed);
      this.draw(live, elapsed);
    }
  }

  /**
   * **Anuncia o que ACABOU de entrar**: cada garra uma vez, e o corte uma vez.
   *
   * Contado por um índice que só sobe, e não por «o tempo caiu nesta janela»: um
   * quadro perdido - uma aba escondida, uma pausa, um pico de GC - saltaria a
   * janela de uma garra e ela ficaria muda. Assim, um salto de trezentos
   * milissegundos anuncia as três garras que passaram, pela ordem delas.
   */
  private announce(live: LiveBurst, elapsed: number): void {
    while (live.announced < live.claws.length) {
      const claw = live.claws[live.announced];
      if (claw === undefined || elapsed < claw.at) {
        break;
      }
      this.audio?.claw(live.announced);
      live.announced += 1;
    }
    if (!live.finaled && elapsed >= bleedFinaleAt(live.burst.marks)) {
      live.finaled = true;
      this.audio?.cut(live.burst.marks);
      this.onFinale?.(live.burst);
    }
  }

  /** Apaga tudo o que está no ecrã, sem o sistema deixar de funcionar. */
  clear(): void {
    for (const [key, live] of this.live) {
      this.retire(key, live);
    }
  }

  /** Larga tudo, o pool incluído. Depois disto não desenha mais nada. */
  destroy(): void {
    this.disposed = true;
    this.clear();
    for (const graphics of this.idle) {
      graphics.destroy();
    }
    this.idle.length = 0;
  }

  private retire(key: string, live: LiveBurst): void {
    live.graphics.clear();
    live.graphics.setVisible(false);
    this.live.delete(key);
    if (this.disposed) {
      live.graphics.destroy();
      return;
    }
    this.idle.push(live.graphics);
  }

  private draw(live: LiveBurst, elapsed: number): void {
    const graphics = live.graphics;
    graphics.clear();
    graphics.setPosition(live.burst.x, live.burst.y);
    const width = bleedMarkWidth(live.burst.diameter);

    if (elapsed < BLEED_CONVERGE_MS) {
      /*
       * **Fase um: as feridas convergem.**
       *
       * Desenhadas como riscos e não como o carácter: um `Graphics` não carimba
       * texturas, e o que importa aqui é o MOVIMENTO - a marca a ser puxada para
       * o casco. A identidade já foi dada pelo 傷 que esteve lá segundos.
       */
      const travel = elapsed / BLEED_CONVERGE_MS;
      for (const place of live.from) {
        const at = { x: place.x * (1 - travel), y: place.y * (1 - travel) };
        graphics
          .fillStyle(BURST_TINT, 1 - travel * 0.3)
          .fillPoints(strokePoints(at, width * 0.6, width * 0.16, -0.6), true, true);
      }
      return;
    }

    /*
     * **Fase dois: as garras, uma por marca, de décimo em décimo de segundo.**
     *
     * Cada uma tem o seu instante de entrada em MILISSEGUNDOS e a sua própria
     * vida, portanto três podem estar no ecrã ao mesmo tempo em fases
     * diferentes - a que acabou de abrir, a que está no pico e a que está a
     * desvanecer. É isso que faz uma sequência ler-se como um ataque em vez de
     * um metrónomo.
     */
    /*
     * O alcance base, medido no CASCO e não na marca.
     *
     * Era `width` - o tamanho de um 傷 - que mandava na espessura, e o diâmetro
     * mandava num alcance de 0,62. O resultado era um risco fino dentro da nave.
     * Agora o casco manda nas duas coisas: um golpe atravessa-o inteiro e tem a
     * espessura de um golpe.
     */
    const reach = live.burst.diameter * BLEED_CLAW_REACH_RATIO;
    const radius = live.burst.diameter / 2;
    const thickness = live.burst.diameter * 0.055;
    for (const claw of live.claws) {
      if (elapsed < claw.at) {
        continue;
      }
      /* A vida DESTA garra: do instante em que entra, e os seus 220 ms. */
      const own = Math.min(1, (elapsed - claw.at) / BLEED_CLAW_LIFE_MS);
      if (own >= 1) {
        continue;
      }
      const punch = Math.sin(own * Math.PI);
      const cos = Math.cos(claw.angle);
      const sin = Math.sin(claw.angle);
      /*
       * O golpe ABRE: começa curto e cresce até ao comprimento próprio.
       *
       * É a diferença entre um risco que aparece e um corte que é dado. O
       * crescimento é rápido no início - `sqrt` - porque uma garra não desacelera
       * a meio.
       */
      const grown = Math.sqrt(Math.min(1, own * 2.2));
      const length = reach * claw.length * (0.35 + 0.65 * grown);
      const fade = 1 - own * own;
      for (let stroke = 0; stroke < CLAW_STROKES; stroke += 1) {
        /* Os três riscos afastados na NORMAL do golpe: paralelos, nunca em leque. */
        const lateral = (stroke - (CLAW_STROKES - 1) / 2) * radius * claw.spread;
        const centre = {
          x: claw.pivot.x * radius - lateral * sin,
          y: claw.pivot.y * radius + lateral * cos,
        };
        graphics
          .fillStyle(CLAW_BODY, 0.95 * fade)
          .fillPoints(
            strokePoints(centre, length, thickness * (1 + 0.35 * punch), claw.angle),
            true,
            true,
          );
        graphics
          .fillStyle(CLAW_CORE, 0.95 * fade)
          .fillPoints(
            strokePoints(centre, length * 0.88, thickness * 0.34, claw.angle),
            true,
            true,
          );
      }
    }

    /*
     * **Fase três: o CORTE, que fecha a sequência.**
     *
     * Um só golpe, e é o único do efeito que cresce com a contagem: as garras
     * dizem quantas marcas foram cobradas - uma cada - e o corte diz quanto isso
     * pesou. Um golpe grande depois de vinte riscos lê-se como a conta a ser
     * fechada; o mesmo golpe depois de um risco lê-se como um risco maior.
     *
     * Desenhado DEPOIS das garras, portanto por cima delas. Duas camadas e não
     * três: corpo carmesim e núcleo quase branco, sem resíduo - o §7 proíbe
     * fragmentos persistentes, e este é o instante mais carregado do efeito.
     */
    const since = elapsed - bleedFinaleAt(live.burst.marks);
    if (since < 0) {
      return;
    }
    const cut = Math.min(1, since / BLEED_FINALE_MS);
    /*
     * **Abre num instante, SEGURA, e só então desaparece.**
     *
     * O defeito que isto corrige: o comprimento crescia com `sqrt(cut)` e o
     * alpha caía com `cut²`, portanto o corte só atingia o tamanho máximo no
     * quadro em que ficava invisível. Ele era grande exatamente quando ninguém o
     * podia ver, e o que se lia era um risco curto e a esbater - o dono disse
     * que "não aparece bem", e não aparecia mesmo.
     *
     * Agora abre no primeiro quinto, fica inteiro e opaco até quase metade da
     * vida, e só depois se apaga. Um golpe é uma coisa que ACONTECE e fica um
     * momento; não é uma coisa que cresce.
     */
    const open = Math.min(1, cut / 0.2);
    const alive = cut < 0.45 ? 1 : 1 - (cut - 0.45) / 0.55;
    const span = bleedFinaleReach(live.burst.diameter, live.burst.marks) * (0.55 + 0.45 * open);
    const bite = bleedFinaleThickness(live.burst.diameter, live.burst.marks);
    /*
     * O ângulo sai da SEMENTE autoritativa, como o das garras: dois clientes a
     * ver o mesmo duelo têm de ver o mesmo corte.
     */
    const angle = finaleAngle(live.burst.seed);
    /*
     * **O clarão do impacto**, no primeiro terço e mais nada.
     *
     * Uma lâmina larga quase branca por baixo do corte, que abre com ele e
     * desaparece antes de o corte acabar. É o que dá o instante da batida - sem
     * ele, o corte entra sem que nada diga que entrou.
     *
     * Terceira camada, e a última: o §7 permite três - núcleo, energia, resíduo -
     * e este é o núcleo, o corpo é a energia, e resíduo não há.
     */
    if (cut < 0.34) {
      const flash = 1 - cut / 0.34;
      graphics
        .fillStyle(CLAW_CORE, 0.85 * flash)
        .fillPoints(
          strokePoints({ x: 0, y: 0 }, span * 1.05, bite * 2.4 * flash, angle),
          true,
          true,
        );
    }
    graphics
      .fillStyle(CLAW_BODY, 0.95 * alive)
      .fillPoints(strokePoints({ x: 0, y: 0 }, span, bite, angle), true, true);
    graphics
      .fillStyle(CLAW_CORE, 0.95 * alive)
      .fillPoints(strokePoints({ x: 0, y: 0 }, span * 0.9, bite * 0.34, angle), true, true);
  }
}

/**
 * **Quanto o corte final atravessa**, para este casco e esta contagem.
 *
 * Cresce com as marcas e PARA: o pedido do dono foi que ele fique maior conforme
 * o número estourado, e o §7 exige um limite ao tamanho. As duas coisas cabem -
 * a contagem manda até ao tecto das garras, e daí em diante manda o casco.
 *
 * Uma marca dá um corte da largura do casco; o tecto dá dois cascos e meio.
 */
export function bleedFinaleReach(diameter: number, marks: number): number {
  const weight = bleedClawCount(marks) / BLEED_MAX_CLAWS;
  return diameter * (1.15 + 1.5 * Math.min(1, weight));
}

/**
 * E a espessura, pela mesma regra e com o mesmo tecto.
 *
 * **Mais do dobro** do que uma primeira versão dava. A 5 % do casco mais 7 % de
 * crescimento, o corte final de uma detonação de cinco marcas media quatro
 * píxeis sobre um casco de sessenta e quatro - mais fino do que as garras que o
 * precediam, quando ele é suposto ser a resposta a elas.
 */
export function bleedFinaleThickness(diameter: number, marks: number): number {
  const weight = bleedClawCount(marks) / BLEED_MAX_CLAWS;
  return diameter * (0.11 + 0.1 * Math.min(1, weight));
}

/**
 * O ângulo do corte final, derivado da semente.
 *
 * Diagonal, como as garras: uma barra vertical ou horizontal sobre um casco
 * lê-se como interface, e não como um golpe.
 */
function finaleAngle(seed: number): number {
  const spun = Math.imul(seed >>> 0 || 1, 2_246_822_519) >>> 0;
  return (-58 + (spun / 0xffff_ffff) * 36) * (Math.PI / 180);
}

/**
 * Um risco: um fuso de pontas agudas, num ângulo.
 *
 * As pontas são agudas por construção - a espessura é `(1 - t²)` e vale zero nos
 * extremos - e não por afinação. Não há `lineStyle` em lado nenhum: um traço tem
 * espessura constante e tampas redondas, que é o contrário de uma garra.
 */
function strokePoints(
  centre: BleedPoint,
  length: number,
  halfThickness: number,
  angle: number,
  samples = 10,
): BleedPoint[] {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const points: BleedPoint[] = [];
  const place = (t: number, side: number): BleedPoint => {
    const along = (t * length) / 2;
    const across = side * halfThickness * (1 - t * t);
    return {
      x: centre.x + along * cos - across * sin,
      y: centre.y + along * sin + across * cos,
    };
  };
  for (let i = 0; i <= samples; i += 1) {
    points.push(place(-1 + (2 * i) / samples, -1));
  }
  for (let i = samples - 1; i >= 1; i -= 1) {
    points.push(place(-1 + (2 * i) / samples, 1));
  }
  return points;
}

/**
 * As garras de um rebentamento, DERIVADAS da semente autoritativa.
 *
 * Nunca `Math.random()`: dois clientes a ver o mesmo duelo têm de ver as mesmas
 * garras, e o §7 exige que a variação saia da identidade ou do tique. O gerador
 * é uma mistura inteira simples - determinística, sem estado e sem dependência
 * de nada fora desta função.
 */
function clawsFrom(seed: number, count: number): Claw[] {
  const claws: Claw[] = [];
  let state = seed >>> 0 || 1;
  const next = (): number => {
    state = (Math.imul(state, 1_664_525) + 1_013_904_223) >>> 0;
    return (state >>> 8) / 0x00ff_ffff;
  };
  for (let i = 0; i < count; i += 1) {
    /*
     * O ponto do casco onde ESTE golpe cai, amostrado no disco.
     *
     * A raiz quadrada do primeiro número é o que distribui os pontos por ÁREA em
     * vez de os concentrar no centro - sem ela, metade dos golpes cairia no mesmo
     * sítio e a nave teria uma mancha no meio em vez de estar arranhada.
     */
    const spin = next() * Math.PI * 2;
    const distance = Math.sqrt(next()) * 0.72;
    claws.push({
      /*
       * Diagonais, e nunca alinhadas: uma garra vertical lê-se como uma barra.
       *
       * A alternância de meia volta dá golpes nos dois sentidos, e o desvio de
       * ±25° em torno da diagonal é o que impede dois arranhões consecutivos de
       * saírem paralelos - que era como se lia quando o ângulo era quase fixo.
       */
      angle: (-55 + (next() - 0.5) * 50) * (Math.PI / 180) + (i % 2 === 0 ? 0 : Math.PI * 0.62),
      pivot: { x: Math.cos(spin) * distance, y: Math.sin(spin) * distance },
      /* Comprimentos desiguais: golpes todos do mesmo tamanho são um padrão. */
      length: 0.72 + next() * 0.55,
      /* E aberturas desiguais entre os três riscos, pela mesma razão. */
      spread: 0.11 + next() * 0.1,
      /*
       * **Uma garra a cada décimo de segundo**, contado em milissegundos desde
       * o início do efeito e não em fração de fase.
       *
       * O intervalo é o pedido do dono e é FIXO: dez marcas demoram um segundo e
       * vinte demoram dois. Uma fração faria vinte marcas caberem no mesmo tempo
       * que duas, apertando-as até deixarem de se contar - que é exatamente o que
       * a versão anterior fazia e o que ele mandou mudar.
       */
      at: BLEED_CONVERGE_MS + i * BLEED_CLAW_INTERVAL_MS,
    });
  }
  return claws;
}
